<template>
  <!-- Only available while impersonating (user_switch_name set). Lets the admin
       toggle feature modules for the current switch session only — the override
       rides in the JWT and is dropped on revert/reload (no DB change). -->
  <v-dialog
    v-if="inSwitch"
    v-model="dialog"
    width="500"
    persistent
    :aria-label="t('user.switch.modules.label')"
  >
    <template #activator="{ props }">
      <v-icon
        v-bind="props"
        class="mr-7 second-menu-item"
        :icon="Icons.COG_OUTLINE"
        :aria-label="t('common.a11y.settings')"
        @click="open()"
        @keydown.enter="open()"
        @keydown.space.prevent="open()"
      />
    </template>
    <v-card>
      <v-card-title>
        {{ t('user.switch.modules.label') }}
      </v-card-title>
      <v-card-text>
        <div class="mb-4">
          {{ t('user.switch.modules.hint') }}
        </div>
        <v-switch
          v-for="moduleItem in moduleItems"
          :key="moduleItem.key"
          v-model="modules[moduleItem.key]"
          :label="moduleItem.label"
          color="primary"
          density="compact"
          hide-details
        />
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="default"
          @click="close()"
        >
          {{ t('common.close') }}
        </v-btn>
        <v-btn
          color="primary"
          :loading="loading"
          @click="apply()"
        >
          {{ t('user.switch.modules.apply') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
// ==================== [JS] ================================
import {UserSwitchApi} from '@/common/api/user/UserSwitchApi';
import {BaseApi} from '@/common/api/BaseApi';
// ==================== [VUE COMPONENTS] ====================
import {computed, ref} from 'vue';
import {ErrorDialog, Icons, verifyResponse} from '@simpleforyou/client-core';
import {useI18n} from 'vue-i18n';
import {useApiClient} from '@/common/api/useApiClient';
import {useStore} from '@/common/store/store';
import {LoginUser} from '@/types/LoginUser';

type ModuleKey =
  'corporateIdentity' | 'course' | 'diary' | 'recipe' | 'statistic'
  | 'exam' | 'notification' | 'liveCoaching' | 'partnerShops';

const {t} = useI18n();
const store = useStore();
const apiClient = useApiClient();

const dialog = ref<boolean>(false);
const loading = ref<boolean>(false);
const modules = ref<Record<ModuleKey, boolean>>({
  corporateIdentity: false,
  course: false,
  diary: false,
  recipe: false,
  statistic: false,
  exam: false,
  notification: false,
  liveCoaching: false,
  partnerShops: false,
});

const inSwitch = computed<boolean>(() => !!store.userData?.user_switch_name);

const moduleItems = computed<{ key: ModuleKey; label: string }[]>(() => [
  {key: 'corporateIdentity', label: t('user.switch.modules.corporateIdentity')},
  {key: 'course', label: t('user.switch.modules.course')},
  {key: 'diary', label: t('user.switch.modules.diary')},
  {key: 'recipe', label: t('user.switch.modules.recipe')},
  {key: 'statistic', label: t('user.switch.modules.statistic')},
  {key: 'exam', label: t('user.switch.modules.exam')},
  {key: 'notification', label: t('user.switch.modules.notification')},
  {key: 'liveCoaching', label: t('user.switch.modules.liveCoaching')},
  {key: 'partnerShops', label: t('user.switch.modules.partnerShops')},
]);

// Seed the switches from the currently effective module state.
const open = () => {
  modules.value = {...store.coaching};
  dialog.value = true;
};

const close = () => {
  dialog.value = false;
};

// Backend DTO uses snake_case keys (mirrors CustomerModulesDto).
const buildPayload = () => ({
  corporate_identity: modules.value.corporateIdentity,
  course: modules.value.course,
  diary: modules.value.diary,
  recipe: modules.value.recipe,
  statistic: modules.value.statistic,
  exam: modules.value.exam,
  notification: modules.value.notification,
  live_coaching: modules.value.liveCoaching,
  partner_shops: modules.value.partnerShops,
});

// Discard cached GET responses so guard-protected data reloads under the new
// module set (same approach as UserSwitch.vue).
const clearApiCache = () => {
  Object.keys(sessionStorage)
    .filter((key) => key.startsWith(BaseApi.ROOT_URL))
    .forEach((key) => sessionStorage.removeItem(key));
};

const apply = async () => {
  loading.value = true;
  const response = await apiClient.post<LoginUser>(UserSwitchApi.modules(), buildPayload()).catch(() => {
    new ErrorDialog();
    loading.value = false;
  });

  if (verifyResponse(response, true)) {
    store.setUserData(response.data.user);
    store.setAccessToken(response.data.accessToken);
    clearApiCache();
    loading.value = false;
    dialog.value = false;
  }
};
</script>
